import React, { useState, useMemo } from 'react';
import { FlashcardConfig, Question } from '../types';

interface FlashcardConfigModalProps {
    isOpen: boolean;
    onStart: (config: FlashcardConfig) => void;
    onCancel: () => void;
    hasHistory: boolean;
    allQuestions: Question[];
    displayLanguage: 'pt' | 'en' | 'es';
}

const FlashcardConfigModal: React.FC<FlashcardConfigModalProps> = ({
    isOpen,
    onStart,
    onCancel,
    hasHistory,
    allQuestions,
    displayLanguage,
}) => {
    const uniqueDomains = useMemo(() => Array.from(new Set(allQuestions.map(q => q.domain).filter((d): d is string => !!d))), [allQuestions]);
    
    const [selectedDomains, setSelectedDomains] = useState<string[]>(() => uniqueDomains);
    const [onlyIncorrect, setOnlyIncorrect] = useState<boolean>(false);
    const [isAllDomainsSelected, setIsAllDomainsSelected] = useState(true);

    const handleDomainToggle = (domain: string) => {
        const newSelection = selectedDomains.includes(domain)
            ? selectedDomains.filter(d => d !== domain)
            : [...selectedDomains, domain];
        setSelectedDomains(newSelection);
        setIsAllDomainsSelected(newSelection.length === uniqueDomains.length);
    };
    
    const handleSelectAllDomains = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecking = e.target.checked;
        setIsAllDomainsSelected(isChecking);
        if (isChecking) {
            setSelectedDomains(uniqueDomains);
        } else {
            setSelectedDomains([]);
        }
    }

    const handleStart = () => {
        onStart({ domains: selectedDomains, onlyIncorrect });
    };

    if (!isOpen) return null;

    const title =
        displayLanguage === 'en'
            ? 'Configure flashcards'
            : displayLanguage === 'es'
              ? 'Configurar flashcards'
              : 'Configurar Flashcards';
    const step1 =
        displayLanguage === 'en'
            ? '1. Filter by domains'
            : displayLanguage === 'es'
              ? '1. Filtrar por dominios'
              : '1. Filtrar por Domínios';
    const selectAllDomains =
        displayLanguage === 'en'
            ? 'Select all domains'
            : displayLanguage === 'es'
              ? 'Seleccionar todos los dominios'
              : 'Selecionar Todos os Domínios';
    const step2 =
        displayLanguage === 'en'
            ? '2. Filter by performance'
            : displayLanguage === 'es'
              ? '2. Filtrar por desempeño'
              : '2. Filtrar por Desempenho';
    const onlyWrong =
        displayLanguage === 'en'
            ? 'Only incorrectly answered questions'
            : displayLanguage === 'es'
              ? 'Solo preguntas respondidas incorrectamente'
              : 'Apenas questões respondidas incorretamente';
    const historyHint =
        displayLanguage === 'en'
            ? 'Complete a quiz to enable this option.'
            : displayLanguage === 'es'
              ? 'Completa un simulado para habilitar esta opción.'
              : 'Complete um simulado para habilitar esta opção.';
    const cancel =
        displayLanguage === 'en' ? 'Cancel' : displayLanguage === 'es' ? 'Cancelar' : 'Cancelar';
    const start =
        displayLanguage === 'en'
            ? 'Start study'
            : displayLanguage === 'es'
              ? 'Iniciar estudio'
              : 'Iniciar Estudo';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex justify-center items-center p-4" aria-modal="true" role="dialog">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-2xl text-left animate-fade-in">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{title}</h2>
                
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-teal-400 mb-3">{step1}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-gray-100 dark:bg-slate-700/50 p-3 rounded-md">
                            <label className="flex items-center cursor-pointer">
                                <input type="checkbox" checked={isAllDomainsSelected} onChange={handleSelectAllDomains} className="form-checkbox h-5 w-5 bg-gray-200 dark:bg-slate-600 border-gray-400 dark:border-slate-500 rounded text-teal-500 focus:ring-teal-500" />
                                <span className="ml-3 font-bold text-gray-800 dark:text-slate-200">{selectAllDomains}</span>
                            </label>
                        </div>
                        {uniqueDomains.map(domain => (
                            <div key={domain} className="bg-gray-100 dark:bg-slate-700/50 p-3 rounded-md">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedDomains.includes(domain)}
                                        onChange={() => handleDomainToggle(domain)}
                                        className="form-checkbox h-5 w-5 bg-gray-200 dark:bg-slate-600 border-gray-400 dark:border-slate-500 rounded text-teal-500 focus:ring-teal-500"
                                    />
                                    <span className="ml-3 text-gray-700 dark:text-slate-300 text-sm">{domain}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-teal-400 mb-3">{step2}</h3>
                     <div className="bg-gray-100 dark:bg-slate-700/50 p-3 rounded-md">
                        <label className={`flex items-center ${hasHistory ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                            <input
                                type="checkbox"
                                checked={onlyIncorrect}
                                onChange={(e) => setOnlyIncorrect(e.target.checked)}
                                disabled={!hasHistory}
                                className="form-checkbox h-5 w-5 bg-gray-200 dark:bg-slate-600 border-gray-400 dark:border-slate-500 rounded text-teal-500 focus:ring-teal-500 disabled:opacity-50"
                            />
                            <span className={`ml-3 text-gray-700 dark:text-slate-300 ${!hasHistory ? 'opacity-50' : ''}`}>{onlyWrong}</span>
                        </label>
                         {!hasHistory && <p className="text-xs text-slate-500 mt-2 ml-8">{historyHint}</p>}
                    </div>
                </div>

                <div className="flex justify-end gap-4">
                    <button onClick={onCancel} className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-slate-600 dark:hover:bg-slate-500 dark:text-white font-bold py-2 px-6 rounded-lg transition-colors">
                        {cancel}
                    </button>
                    <button onClick={handleStart} disabled={selectedDomains.length === 0} className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:bg-slate-600 disabled:cursor-not-allowed">
                        {start}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FlashcardConfigModal;
